import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useUser } from '@store';

interface UnassignDriverDialogProps {
  open: boolean;
  onClose: () => void;
  runId: number;
  /** The driver currently on the run — accepted OR just offered. Shown in
   *  the confirmation body so the coordinator knows who they're removing. */
  currentDriverName?: string | null;
}

/**
 * Confirm-only dialog for removing a technician from a mobile-service run
 * without immediately reassigning to someone else. POSTs to the same
 * reassignMobileServiceRun Netlify function as the reassign dialog, but with
 * no `newDriverId` — the server branches to the unassign path (deprecate
 * bundles, clear driver_id, set status to 'draft').
 *
 * Parallel to ReassignDriverDialog. The driver picker doesn't apply here
 * because the coordinator has explicitly chosen not to retarget.
 */
const UnassignDriverDialog: React.FC<UnassignDriverDialogProps> = ({
  open,
  onClose,
  runId,
  currentDriverName,
}) => {
  const user = useUser();
  const [submitting, setSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const token = await user?.getToken();
      if (!token) {
        throw new Error('Authentication failed — could not get token');
      }
      const baseUrl = window.location.origin;
      const res = await axios({
        method: 'POST',
        url: `${baseUrl}/.netlify/functions/reassignMobileServiceRun`,
        data: { runId }, // no newDriverId → unassign mode
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Unassign returned non-success');
      }
      toast.success(
        currentDriverName
          ? `${currentDriverName} unassigned from this run`
          : 'Run unassigned',
      );
      onClose();
    } catch (err: any) {
      // Server returns 409 on already-started runs with a friendly message.
      const serverMsg = err?.response?.data?.error;
      toast.error(serverMsg || err?.message || 'Unassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Unassign technician</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            {currentDriverName ? (
              <>
                Remove <strong>{currentDriverName}</strong> from this run?
              </>
            ) : (
              <>Remove the current technician from this run?</>
            )}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            The technician will be notified that the run was taken off their list.
            The run will move back to <strong>draft</strong> and won't be offered
            again until you redispatch it.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='warning'
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {submitting ? 'Unassigning…' : 'Unassign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnassignDriverDialog;
